import React from 'react'
import {
  useBlockLayout,
  usePagination,
  useResizeColumns,
  useRowSelect,
  useRowState,
  useSortBy,
  useTable
} from 'react-table'
import { useSticky } from 'react-table-sticky'
import styled from 'styled-components'
import { computeTWidth, manipulateHooks, stateReducer } from '..'
import { Flex, Span } from '../../main'
import NoData from '../components/NoData'
import Pagination from '../components/Pagination'
import SortIcons from '../components/Sorticons'
import TableActions from './TableActions'

const defaultPageOptions = [10, 25, 50, 100, 150, 200]

const StickyTableStyle: any = styled.div`
  width: 100%;
  .sc-sticky-table {
    border: 1px solid #f0f0f0;
    box-sizing: border-box;
    overflow: scroll;
    .sc-sticky-header,
    .sc-sticky-footer {
      position: sticky;
      /* z-index: 1; */
      width: fit-content;
    }
    .sc-sticky-header {
      top: 0;
      box-shadow: 0px 1px 0px #ccc;
    }
    .sc-sticky-footer {
      bottom: 0;
      box-shadow: 0px -1px 0px #ccc;
    }
    .sc-sticky-body {
      position: relative;
      z-index: 0;
    }
    [data-sticky-td] {
      position: sticky;
      font-family: 'Roboto';
    }
    [data-sticky-last-left-td] {
      box-shadow: 1px 0px 0px #ccc;
    }
    [data-sticky-first-right-td] {
      box-shadow: -1px 0px 0px #ccc;
    }
    .sc-sticky-tr {
      background: #fff;
      &:hover {
        .sc-sticky-td {
          background: #e5f6ff;
          transition: background 0.3s ease;
        }
      }
      :last-child {
        .sc-sticky-td {
          border-bottom: 0;
        }
      }
    }
    .sc-sticky-th,
    .sc-sticky-td {
      font-family: 'Roboto';
      box-sizing: border-box;
      border-bottom: 1px solid #f0f0f0;
      border-right: 1px solid #ececec;
      background: #fff;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-size: ${(props: any) => {
        switch (props.fontSize) {
          case 'xxs':
            return '9px'
          case 'xs':
            return '10px'
          case 's':
            return '11px'
          case 'm':
            return '12px'
          case 'l':
            return '13px'
          case 'xl':
            return '14px'
          case 'xxl':
            return '15px'
        }
      }};
      padding: ${(props: any) => {
        switch (props.size) {
          case 'xxs':
            return '0'
          case 'xs':
            return '4px'
          case 's':
            return '6px'
          case 'm':
            return '8px'
          case 'l':
            return '10px'
          case 'xl':
            return '12px'
          case 'xxl':
            return '14px'
        }
      }};
      :last-child {
        border-right: 0;
      }
    }
    .sc-sticky-th {
      text-transform: uppercase;
      background: #eff6ff;
      color: #727272;
      font-weight: 300;
      letter-spacing: 0.4px;
      .resizer {
        display: inline-block;
        width: 5px;
        height: 100%;
        position: absolute;
        right: 0;
        top: 0;
        transform: translateX(50%);
        z-index: 1;
        &.isResizing {
          background: #e9f0f9;
        }
      }
    }
  }
`

const StickyHead = ({
  headerGroups,
  sortable,
  onSortedChange,
  resizableColumns
}: any) => {
  return (
    <div className="sc-sticky-header">
      {headerGroups.map((headerGroup: any, i: number) => (
        <div
          data-testid={`thead-tr-${i}`}
          {...headerGroup.getHeaderGroupProps()}
          className="sc-sticky-tr"
          key={`thead-tr-${i}`}
        >
          {headerGroup.headers.map((column: any, j: number) => {
            const thProps = column.getHeaderProps()
            const style = {
              ...thProps.style,
              maxWidth: column.maxWidth,
              minWidth: column.minWidth,
              width: column.width
            }
            const isColumnSortable =
              sortable && (column.canSort || column.sortable)

            return (
              <div
                {...thProps}
                style={style}
                data-testid={`th-${i}-${j}`}
                className="sc-sticky-th"
                key={`th-${i}-${j}`}
              >
                <span
                  style={isColumnSortable ? { cursor: 'pointer' } : {}}
                  onClick={(e: any) => {
                    if (isColumnSortable) {
                      if (onSortedChange) {
                        onSortedChange([
                          { id: column.id, desc: !!column.isSortedDesc }
                        ])
                      } else if (
                        column.getHeaderProps(column.getSortByToggleProps())
                          .onClick
                      ) {
                        column
                          .getHeaderProps(column.getSortByToggleProps())
                          .onClick(e)
                      }
                    }
                  }}
                >
                  {column.render('Header')}
                  {isColumnSortable ? (
                    <SortIcons column={column} size={12} />
                  ) : null}
                </span>
                {resizableColumns ? (
                  <div
                    data-testid={`resizer-${i}-${j}`}
                    {...column.getResizerProps()}
                    className={`resizer ${
                      column.isResizing ? 'isResizing' : ''
                    }`}
                  />
                ) : null}
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}

export function StickyTable(props: any) {
  const {
    data = [],
    dataLoading,
    columns,
    sticky,
    className,
    style,
    size = 'm',
    fontSize = 'm',
    refresh,
    refreshing,
    refreshLabel,
    onPageChange,
    defaultPageSize,
    defaultPageIndex,
    onPageSizeChange,
    disablePageSizeChange = false,
    defaultSelectedRowIds,
    getTrProps,
    getTdProps,
    showRowSelect,
    setSelectedRows,
    downloadCSV,
    downloadLabel,
    downloadTitle,
    downloadLoading = false,
    downloadDisabled,
    expandable,
    subComponent,
    totalCountText,
    sortable,
    totalElements,
    showTotalCount = true,
    onSortedChange,
    resizableColumns = true,
    commentingModule,
    issueLabel,
    manualPagination = false,
    showPagination = true,
    t,
    selectedFilters,
    setSelectedFilters,
    defaultPageSizeOptions = defaultPageOptions
  } = props

  var ins: any = useTable(
    {
      data,
      columns,
      stateReducer: (ns, a, ps) =>
        stateReducer(ns, a, ps, {
          defaultPageSize,
          manualPagination,
          defaultPageIndex,
          defaultPageSizeOptions,
          defaultSelectedRowIds
        })
    },
    useSortBy,
    useRowState,
    usePagination,
    useRowSelect,
    useResizeColumns,
    useBlockLayout,
    useSticky,
    hooks => manipulateHooks({ hooks, showRowSelect, expandable, sticky })
  )
  const {
    rows = [],
    page,
    gotoPage,
    nextPage,
    pageCount,
    allColumns,
    canNextPage,
    pageOptions,
    setPageSize,
    headerGroups,
    previousPage,
    getTableProps,
    canPreviousPage,
    selectedFlatRows,
    isAllRowsSelected,
    isAllPageRowsSelected,
    getTableBodyProps,
    totalColumnsWidth,
    totalColumnsMaxWidth,
    totalColumnsMinWidth,
    state: { pageIndex, pageSize, selectedRowIds },
    prepareRow
  } = ins

  React.useEffect(() => {
    if (!showPagination && pageSize !== data.length) {
      setPageSize(data.length)
    } else {
      setPageSize(defaultPageSize || defaultPageSizeOptions[0])
    }
  }, [showPagination])

  React.useEffect(() => {
    setSelectedRows?.({
      isAllPageRowsSelected,
      isAllRowsSelected,
      selectedRowIds: Object.keys(selectedRowIds),
      selectedFlatRows
    })
  }, [isAllRowsSelected, selectedRowIds])

  const renderData = showPagination ? page : rows
  const hasDataForCurrPageIndex = showPagination
    ? pageIndex * pageSize < data.length
    : !!renderData.length
  const tableMaxWidth = computeTWidth(headerGroups)
  const totalDataLength = totalElements || data.length

  return (
    <StickyTableStyle
      data-testid="table"
      size={size}
      style={{ ...(style?.wrapper || {}) }}
      fontSize={fontSize}
      className={`${className}`}
    >
      <TableActions
        refresh={refresh}
        refreshing={refreshing}
        refreshLabel={refreshLabel}
        downloadCSV={downloadCSV}
        downloadLabel={downloadLabel}
        showTotalCount={showTotalCount}
        totalCountText={totalCountText}
        totalElements={totalDataLength}
        downloadTitle={downloadTitle}
        downloadDisabled={downloadDisabled}
        downloadLoading={downloadLoading}
        commentingModule={commentingModule}
        issueLabel={issueLabel}
        t={t}
        selectedFilters={selectedFilters}
        setSelectedFilters={setSelectedFilters}
      />
      <div
        {...getTableProps()}
        className="sc-sticky-table"
        style={{ ...getTableProps().style, ...(style?.table || {}) }}
      >
        <StickyHead
          sortable={sortable}
          headerGroups={headerGroups}
          onSortedChange={onSortedChange}
          resizableColumns={resizableColumns}
        />
        <div
          {...getTableBodyProps()}
          style={{ ...(style?.body || {}) }}
          className="sc-sticky-body"
        >
          {hasDataForCurrPageIndex ? (
            renderData.map((row: any, trIndex: number) => {
              prepareRow(row)
              const trProps = {
                ['data-testid']: `tbody-tr-${trIndex}`,
                ...row.getRowProps(),
                ...(style?.wrapper || {}),
                ...(getTrProps ? getTrProps(row) : {})
              }
              var showSubComp = !!subComponent
              if (expandable && subComponent) {
                showSubComp = Object.keys(selectedRowIds).includes(row.id)
              }
              const rowWidth = trProps.style?.width
              return (
                <React.Fragment key={trIndex}>
                  <div {...trProps} className="sc-sticky-tr">
                    {row.cells.map((cell: any, j: number) => {
                      const tdProps = {
                        ['data-testid']: `tbody-td-${trIndex}-${j}`,
                        ...cell.getCellProps(),
                        ...(getTdProps ? getTdProps(cell) : {})
                      }

                      return (
                        <div key={j} {...tdProps} className="sc-sticky-td">
                          {cell.render('Cell')}
                        </div>
                      )
                    })}
                  </div>
                  {showSubComp ? (
                    <div
                      className="sub-comp"
                      style={{ width: rowWidth }}
                      data-testid={`tbody-tr-sub-${trIndex}`}
                    >
                      <div
                        data-testid={`tbody-subcomp-${trIndex}`}
                        style={{ borderBottom: '8px solid #f0f2f5' }}
                      >
                        {subComponent?.({
                          index: row.index,
                          original: row.original,
                          row,
                          level: row.depth,
                          page: pageIndex,
                          pageSize
                        })}
                      </div>
                    </div>
                  ) : null}
                </React.Fragment>
              )
            })
          ) : (
            <NoData
              noDataLoading={
                dataLoading || (!hasDataForCurrPageIndex && !!refreshing)
              }
              colSpan={allColumns.length}
            />
          )}
        </div>
      </div>
      <Flex alignItemsCenter justifyContentFlexEnd>
        <Span>{dataLoading && totalDataLength ? 'Loading...' : null}</Span>
        <Pagination
          gotoPage={(pageNo: number) => {
            gotoPage(pageNo)
            onPageChange?.(pageNo)
          }}
          pageSize={pageSize}
          pageIndex={pageIndex}
          canNextPage={canNextPage}
          setPageSize={(changedPageSize: number) => {
            setPageSize(changedPageSize)
            onPageChange?.(0)
            onPageSizeChange?.(changedPageSize)
          }}
          dataLoading={dataLoading}
          disablePageSizeChange={disablePageSizeChange}
          totalElements={totalDataLength}
          pageOptions={defaultPageSizeOptions}
          showPagination={showPagination}
          canPreviousPage={canPreviousPage}
        />
      </Flex>
    </StickyTableStyle>
  )
}
